import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomErrors } from '@asamco/base';
import { ParameterType as ASParameterType } from '@asamco/management-portal/shared';
import { FormHelpers, showErrorSwal } from '@asamco/utils';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { ApplicationLoginStatus } from '../../enums/application-login-status.enum';
import { ApplicationLoginType } from '../../enums/application-login-type.enum';
import { LoginFieldType } from '../../enums/login-field-type.enum';
import { ParameterType as ALParameterType } from '../../enums/parameter-type.enum';
import { ApplicationParams } from '../../interfaces/application-login-params.interface';
import { ApplicationLogin } from '../../interfaces/application-login.interface';
import { LoginField } from '../../interfaces/login-field.interface';
import { ApplicationLoginService } from '../../services/application-login.service';
import { DxiButtonModule } from 'devextreme-angular/ui/nested';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTextBoxModule, DxNumberBoxModule, DxSelectBoxModule, DxCheckBoxModule, DxSwitchModule } from 'devextreme-angular';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgClass, AsyncPipe, JsonPipe } from '@angular/common';
import { AuthFlowModalComponent as AuthFlowModalComponent_1 } from '../../../../../shared/src/lib/components/auth-flow-modal/auth-flow-modal.component';
import { LinkEditApplicationLoginFieldsComponent } from './link-edit-application-login-fields/link-edit-application-login-fields.component';
import { LinkEditApplicationLoginAuthComponent } from '../link-edit-application-login-auth/link-edit-application-login-auth.component';
import { LoginSaveAction } from '../../enums/login-save-action.enum';

@Component({
    selector: 'al-link-edit-application-login-modal',
    templateUrl: './link-edit-application-login-modal.component.html',
    styleUrls: ['./link-edit-application-login-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AuthFlowModalComponent_1,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        DxTextBoxModule,
        AsValidatorDirective,
        DxiButtonModule,
        DxNumberBoxModule,
        DxSelectBoxModule,
        NgSwitch,
        NgSwitchCase,
        NgClass,
        DxCheckBoxModule,
        AsyncPipe,
		DxSwitchModule,
		LinkEditApplicationLoginFieldsComponent,
		LinkEditApplicationLoginAuthComponent,
		JsonPipe
    ],
})
export class LinkEditApplicationLoginModalComponent implements OnDestroy {
	@Output() loginSaved = new EventEmitter<{appLogin: ApplicationLogin, appSide: string, action: LoginSaveAction}>();

	@Input()
	formGroup: FormGroup;

	@Input()
	loginType: ApplicationLoginType;

	@Input()
	applicationId: number;

	@Input()
	fromLinkType: string;

	@Input()
	initLogin: ApplicationLogin;

	@Input()
	linkId: number;

	//Either to or from
	@Input()
	appSide: string;

	@Input()
	public appParams: ApplicationParams;

	public showDialog$: Subject<boolean> = new BehaviorSubject<boolean>(false);

	loginFieldType = LoginFieldType;
	loginFields: LoginField[];

	toolTipVisible: boolean = false;

	public readonly asParameterType = ASParameterType;
	public readonly alParameterType = ALParameterType;

	public customErrors: CustomErrors = {
		ERROR_REQUIRED: 'This field is required',
		ERROR_MAXLENGTH: 'This field exceeds the maximum allowed amount of characters',
	};

	/** Boolean for indicating when the form is initialized */
	public loginValiditySubject: BehaviorSubject<ApplicationLoginStatus> = new BehaviorSubject<ApplicationLoginStatus>(
		ApplicationLoginStatus.undefined
	);
	public doesLoginHaveId: Subject<boolean> = new BehaviorSubject<boolean>(true);
	public doesLoginHaveId$: Observable<boolean> = this.doesLoginHaveId.asObservable();

	disabledLoginBtnSubject = new Subject<boolean>();
	disabledLoginBtnObservable = this.disabledLoginBtnSubject.asObservable();

	private destroy$: Subject<void> = new Subject<void>();

	public readonly passwordTextBoxMode$ = new BehaviorSubject<'text' | 'password'>('password');
	public readonly passwordIcon$ = new BehaviorSubject<'far fa-eye' | 'far fa-eye-slash'>('far fa-eye');

	public passwordButtonOptions = {
		type: 'as-password-toggle',
		icon: this.passwordIcon$.value,
		onClick: () => {
			this.passwordIcon$.next(this.passwordIcon$.value === 'far fa-eye' ? 'far fa-eye-slash' : 'far fa-eye');
			this.passwordTextBoxMode$.next(this.passwordTextBoxMode$.value === 'text' ? 'password' : 'text');
		},
	};

	constructor(
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		private appLoginService: ApplicationLoginService,
		private cdr: ChangeDetectorRef
	) {}

	public onPasswordToggleClicked($event) {}

	/* ngOnInit(): void {
		//this.InitFormGroup();
		if(this.formGroup)
		{
			if(this.appParams?.parameters) {
				this.initializeParameterForm(this.appParams.parameters);
			}
			this.checkFormGroupForLeadingOrTrailingSpace();
		}

		if (this.initLogin) this.setLogin(this.initLogin);
	} */

	parseBoolean(value: string): boolean {
		return value.toLowerCase() === 'true' || value === '1';
	  }

	toggleIconTooltip(e) {
		e.preventDefault();
		this.toolTipVisible = !this.toolTipVisible;
	}

	dropDownValueChanged(event) {
		console.log(event);
	}

	public saveAction = () => {
		//If we have a parameter with underlying elements, we'll need to check if that parameter is enabled/visible.
		//If it's enabled, we'll need to include the underlying elements in the login object, otherwise we'll need to remove them.
		//This is because the underlying elements are only visible when the parent parameter is enabled.

		const login: ApplicationLogin = this.formGroup.value;
		login.applicationId = this.applicationId;
		if (!login.id) login.id = 0;

		if (login.id > 0) {
			return this.appLoginService.updateExistingLogin(this.linkId, this.appSide, login).pipe(tap(result => {
				this.loginSaved.emit({appLogin: this.propertiesToLowerCase(this.formGroup.value), appSide: this.appSide, action: LoginSaveAction.Saved});
			}));
		} else {
			return this.appLoginService.createNewLogin(this.linkId, this.appSide, login).pipe(tap(result => {
				this.formGroup.get('id').setValue(result.id);
				this.doesLoginHaveId.next(true);
				this.loginSaved.emit({appLogin: this.propertiesToLowerCase(this.formGroup.value), appSide: this.appSide, action: LoginSaveAction.Created});
			}));
		}
	}

	propertiesToLowerCase(object) {
		const newObject: any = {};
		for(const key of Object.keys(object)) {
			newObject[key.toLowerCase()] = object[key];
		}
		return newObject;
	}

	public savebtn(): void {
		if (FormHelpers.validateForm(this.formGroup)) {
			this.disabledLoginBtnSubject.next(true);

			this.saveAction().subscribe({
				next: result => {
					if (result.success) {
						this.showDialog$.next(false);

						Swal.fire({
							icon: 'success',
							text: `Successfully ${this.formGroup.value.id ? 'updated' : 'created'} login`,
						});
					}

					this.disabledLoginBtnSubject.next(false);
				},
				error: error => {
					showErrorSwal(error);
					this.disabledLoginBtnSubject.next(false);
				}
			});
		}
	}

	public show(formGroup: FormGroup, loginType: ApplicationLoginType, applicationId: number, fromLinkType: string, initLogin: ApplicationLogin, appSide: string, linkId: number, appParams: ApplicationParams) {
		this.formGroup = formGroup;
		this.loginType = loginType;
		this.applicationId = applicationId;
		this.fromLinkType = fromLinkType;
		this.initLogin = initLogin;
		this.appSide = appSide;
		this.linkId = linkId;
		this.appParams = appParams;

		if (this.initLogin && this.initLogin.id > 0) {
			this.doesLoginHaveId.next(true);
		} else {
			this.doesLoginHaveId.next(false);
		}

		this.showDialog$.next(true);
	}

	public cancel(): void {
		this.loginSaved.emit({appLogin: null, appSide: this.appSide, action: LoginSaveAction.Cancelled});
		this.showDialog$.next(false);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
